import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
  Card, Alert, Spinner, Select, useChartTheme, useIsMobile, monthAxes, monthChartHeight, barRadius, compactTry,
} from '../../../core/ui';
import { debtForecast } from '../api/budget';
import { formatTry } from '../format';
import { useTranslation } from '../../../core/context/TranslationContext';
import styles from './DebtForecastChart.module.css';

export default function DebtForecastChart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chart = useChartTheme();
  const vertical = useIsMobile();

  const [months, setMonths] = useState(12);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await debtForecast(months);
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || t('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [months, t]);

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: chart.bgElevated,
      borderColor: chart.border,
      borderWidth: 1,
      textStyle: { color: chart.text, fontSize: 12 },
      formatter: (params) => {
        const param = params[0];
        const row = rows[param.dataIndex];
        if (!row) return '';
        let html = `<div style="font-weight:600;margin-bottom:6px">${row.month} — ${formatTry(row.totalDueTry)}</div>`;
        if (row.installments && row.installments.length > 0) {
          html += '<div style="font-size:12px">';
          for (const inst of row.installments) {
            html +=
              `<div style="display:flex;justify-content:space-between;gap:20px;padding:3px 0;border-bottom:1px solid ${chart.border}">` +
              `<span style="color:${chart.text}">${inst.merchantNormalized}</span>` +
              `<span style="color:${chart.palette[0]};font-weight:500;white-space:nowrap">${formatTry(inst.monthlyAmount)}</span>` +
              `</div>`;
          }
          html += '</div>';
        } else {
          html += `<div style="color:${chart.textMuted};font-size:12px">${t('noData')}</div>`;
        }
        html += `<div style="margin-top:7px;font-size:11px;color:${chart.textMuted}">${t('budget.clickToViewInstallments')}</div>`;
        return html;
      },
    },
    grid: { left: '3%', right: '4%', bottom: vertical ? '3%' : '10%', top: vertical ? 16 : 60, containLabel: true },
    ...monthAxes(vertical, {
      type: 'category',
      data: rows.map((r) => r.month),
      axisLabel: { color: chart.textMuted, rotate: -30, fontSize: 11 },
      axisLine: { lineStyle: { color: chart.border } },
      axisTick: { show: false },
    }, {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: chart.border } },
      axisLabel: {
        color: chart.textMuted,
        fontSize: 11,
        formatter: compactTry,
      },
    }),
    series: [
      {
        type: 'bar',
        data: rows.map((r) => Number(r.totalDueTry)),
        cursor: 'pointer',
        itemStyle: {
          color: chart.palette[2], /* warning hue for upcoming debt */
          borderRadius: barRadius(vertical),
        },
        barMaxWidth: 40,
      },
    ],
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('budget.reportDebtForecast')}</Card.Title>
        <div className={styles.headerControl}>
          <Select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            aria-label={t('budget.reportDebtForecast')}
          >
            <option value={6}>{t('budget.sixMonths')}</option>
            <option value={12}>{t('budget.twelveMonths')}</option>
            <option value={24}>{t('budget.twentyFourMonths')}</option>
          </Select>
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <Spinner size="sm" />}
        {rows.length === 0 && !loading ? (
          <div className={styles.empty}>{t('noData')}</div>
        ) : (
          <ReactECharts
            option={option}
            style={{ height: monthChartHeight(vertical, rows.length, 400) }}
            onEvents={{
              click: (params) => {
                const row = rows[params?.dataIndex];
                if (!row?.month) { navigate('/budget/installments'); return; }
                navigate(`/budget/installments?month=${row.month.substring(0, 7)}`);
              },
            }}
          />
        )}
      </Card.Body>
    </Card>
  );
}
